import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { LearningQueueApi } from 'src/api';
import { LearningQueueResponse } from 'src/api/learningQueue/types';
import { getLearningFeaturesKey } from 'src/apps/MyLessons/getLessonsKey';

const MAX_RETRIES = 5;
const BASE_INTERVAL = 500;

export const useLatestData = ({
  dataUpdateKey,
  forceRefreshKey,
  enabled: enabledFromParam,
  expectedFeaturesKey,
}: {
  dataUpdateKey: string;
  forceRefreshKey: string;
  enabled: boolean;
  expectedFeaturesKey?: string | null;
}) => {
  const languagePairId = getLanguagePairId();
  const [retries, setRetries] = useState(0);
  const [prevLearningQueue, setPrevLearningQueue] = useState<LearningQueueResponse | undefined>(
    undefined,
  );
  const lastLearningQueue = useRef<LearningQueueResponse | undefined>(undefined);
  const [isRefetchStopped, setIsRefetchStopped] = useState<boolean>(false);

  const enabled = Boolean(
    enabledFromParam && languagePairId && !isRefetchStopped && retries < MAX_RETRIES,
  );

  const { data: learningQueue } = useQuery({
    queryKey: ['getLearningQueue', languagePairId, forceRefreshKey],
    queryFn: () => {
      setRetries(r => r + 1);
      return LearningQueueApi.getLearningQueue(languagePairId as number);
    },
    enabled,
    refetchInterval: BASE_INTERVAL,
  });

  /* istanbul ignore next: please test in WEBSITE-7219 */
  const isExpectedLearningQueue = useCallback(
    (prevQueue: LearningQueueResponse, newQueue: LearningQueueResponse) => {
      if (expectedFeaturesKey) {
        const newFeaturesKey = getLearningFeaturesKey(newQueue.learningFeatures);
        return newFeaturesKey && newFeaturesKey.startsWith(expectedFeaturesKey);
      }

      return newQueue.lastUpdated !== prevQueue.lastUpdated;
    },
    [expectedFeaturesKey],
  );

  useEffect(() => {
    if (!learningQueue) return;

    lastLearningQueue.current = learningQueue;

    if (!prevLearningQueue || isExpectedLearningQueue(prevLearningQueue, learningQueue)) {
      setIsRefetchStopped(true);
      setPrevLearningQueue(undefined);
    }
  }, [learningQueue, prevLearningQueue, isExpectedLearningQueue]);

  useEffect(() => {
    if (!lastLearningQueue.current) return;

    setRetries(0);
    setIsRefetchStopped(false);
    setPrevLearningQueue(lastLearningQueue.current);
  }, [dataUpdateKey]);

  useEffect(() => {
    setRetries(0);
    setIsRefetchStopped(false);
    setPrevLearningQueue(undefined);
  }, [forceRefreshKey]);

  const learningFeatures = (prevLearningQueue || learningQueue)?.learningFeatures;

  if (learningFeatures && learningFeatures.length === 0) {
    return {
      learningFeatures: undefined,
      isLoading: false,
      isError: true,
    };
  }

  const isError = retries >= MAX_RETRIES;
  const isLoading = !isError && !learningFeatures;

  return {
    learningFeatures,
    isLoading,
    isError,
  };
};
